// src/posts/GradientCheckpointing.jsx
// Scaling Training · Part 1 of 3 — Gradient Checkpointing.
// TAGS FOR REGISTRATION: ['training', 'memory', 'transformers']
// EXCERPT: Trade FLOPs for memory: drop most activations during the forward pass and recompute them on backward. The √n placement that turns O(n) memory into O(√n) for ~30% extra compute.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import usePageMeta from '../usePageMeta';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import './PostChrome.css';
import './GradientCheckpointing.css';

/* ============================================================
   KaTeX wrapper — uses the CDN global window.katex
   ============================================================ */
function Katex({ tex, block = false, className = '' }) {
  const ref = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const render = () => {
      if (cancelled || !ref.current) return;
      if (window.katex) {
        try {
          window.katex.render(tex, ref.current, { displayMode: block, throwOnError: false });
        } catch {
          if (ref.current) ref.current.textContent = tex;
        }
      } else {
        setTimeout(render, 60);
      }
    };
    render();
    return () => { cancelled = true; };
  }, [tex, block]);
  return block
    ? <div ref={ref} className={`gc-katex-block ${className}`} />
    : <span ref={ref} className={`gc-katex-inline ${className}`} />;
}

/* ============================================================
   Widget 1 — Interactive trade-off
   n-layer chain. Slider sets number of stored checkpoints k.
   Shows live memory (k + ceil(n/k)) and extra forward FLOPs.
   ============================================================ */
function TradeOff() {
  const N = 16;
  const [k, setK] = useState(4); // √16 = 4
  const W = 760, H = 220;
  const padX = 30, topY = 56, boxH = 36, gapY = 22;

  // Place k checkpoints evenly along the chain at indices 0..N-1.
  // Convention: indices 0 and N-1 are always endpoints (input + output),
  // and we additionally store k interior checkpoints between them.
  const stored = useMemo(() => {
    const s = new Set([0, N - 1]);
    if (k <= 0) return s;
    const stride = (N - 1) / (k + 1);
    for (let j = 1; j <= k; j++) s.add(Math.round(j * stride));
    return s;
  }, [k]);

  // Largest "gap" — max number of layers between two consecutive stored
  // activations. That sets peak working memory during recompute.
  const gap = useMemo(() => {
    const arr = [...stored].sort((a, b) => a - b);
    let m = 0;
    for (let i = 1; i < arr.length; i++) m = Math.max(m, arr[i] - arr[i - 1]);
    return m;
  }, [stored]);

  const peakMem = stored.size + gap; // stored + one segment's recompute buffer
  const baselineMem = N + 1;
  const extraFlops = N - stored.size; // one extra forward worth, minus what's stored
  const baselineFlops = 3 * N; // 1 forward + 2 backward (≈2× forward) = 3 forward-units
  const slowdownPct = (extraFlops / baselineFlops) * 100;

  const cellW = (W - 2 * padX) / N;

  return (
    <div className="viz-panel gc-tradeoff">
      <div className="gc-controls">
        <label>
          Stored checkpoints{' '}
          <span className="gc-val">k = {k}</span>
          {' '}<span className="gc-mut">(extra interior)</span>
        </label>
        <input
          type="range" min={0} max={N - 2} step={1}
          value={k} onChange={(e) => setK(+e.target.value)}
          className="gc-range"
        />
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="gc-chain" preserveAspectRatio="xMidYMid meet">
        {/* Backward arrow track */}
        <line x1={padX} y1={topY + boxH + gapY} x2={W - padX} y2={topY + boxH + gapY}
              stroke="var(--post-border)" strokeWidth="1" strokeDasharray="3 4" />
        <text x={W - padX} y={topY + boxH + gapY - 6} textAnchor="end" fontSize="10"
              fill="var(--post-text-mut)" fontFamily="var(--post-mono)">backward pass</text>

        {Array.from({ length: N }).map((_, i) => {
          const x = padX + i * cellW + 2;
          const isStored = stored.has(i);
          return (
            <g key={i}>
              <rect
                x={x} y={topY}
                width={cellW - 4} height={boxH}
                rx={5}
                className={`gc-cell ${isStored ? 'is-stored' : 'is-recomp'}`}
              />
              <text
                x={x + (cellW - 4) / 2}
                y={topY + boxH / 2 + 4}
                textAnchor="middle"
                fontSize="11"
                fontFamily="var(--post-mono)"
                fill={isStored ? '#fff' : 'var(--post-text-mut)'}
              >
                {i + 1}
              </text>
            </g>
          );
        })}

        {/* Legend */}
        <g transform={`translate(${padX}, ${topY + boxH + gapY + 24})`}>
          <rect x={0} y={0} width={14} height={14} rx={3} className="gc-cell is-stored" />
          <text x={20} y={11} fontSize="11" fill="var(--post-text-mid)">stored on forward</text>
          <rect x={150} y={0} width={14} height={14} rx={3} className="gc-cell is-recomp" />
          <text x={170} y={11} fontSize="11" fill="var(--post-text-mid)">recomputed on backward</text>
        </g>

        {/* Forward-pass label */}
        <text x={padX} y={topY - 14} fontSize="10" fill="var(--post-text-mut)"
              fontFamily="var(--post-mono)">forward pass →</text>
      </svg>

      <div className="gc-kpis">
        <div className="gc-kpi">
          <div className="gc-kpi-label">Peak activation memory</div>
          <div className="gc-kpi-val">{peakMem}</div>
          <div className="gc-kpi-sub">
            = {stored.size} stored + {gap} recompute buffer ·{' '}
            <span className="gc-mut">baseline {baselineMem}</span>
          </div>
        </div>
        <div className="gc-kpi">
          <div className="gc-kpi-label">Extra forward FLOPs</div>
          <div className="gc-kpi-val">{extraFlops}</div>
          <div className="gc-kpi-sub">
            ≈ {slowdownPct.toFixed(0)}% slowdown ·{' '}
            <span className="gc-mut">baseline {baselineFlops} (1F + 2B)</span>
          </div>
        </div>
      </div>
      <p className="gc-caption">
        Slide <em>k</em> from 0 (recompute everything from the input — minimal memory,
        maximal cost) up to 14 (store almost everything — full memory, zero overhead).
        The sweet spot sits near <Katex tex="k \approx \sqrt{n}" />: about 4 stored
        checkpoints for a 16-layer chain.
      </p>
    </div>
  );
}

/* ============================================================
   Widget 2 — Scaling curves
   Peak memory vs depth for the three regimes: O(n), O(√n), O(log n).
   Log-log axes.
   ============================================================ */
function ScalingChart() {
  const W = 760, H = 340;
  const padL = 56, padR = 18, padT = 24, padB = 44;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  // n axis: 8 → 2048, log scale
  const nMin = 8, nMax = 2048;
  const memMin = 1, memMax = 2048;
  const xOf = (n) => padL + (Math.log2(n) - Math.log2(nMin)) / (Math.log2(nMax) - Math.log2(nMin)) * innerW;
  const yOf = (m) => padT + innerH - (Math.log2(m) - Math.log2(memMin)) / (Math.log2(memMax) - Math.log2(memMin)) * innerH;

  const ns = [];
  for (let n = nMin; n <= nMax; n *= 1.2) ns.push(n);
  ns.push(nMax);

  const pathFor = (fn) => ns.map((n, i) => `${i === 0 ? 'M' : 'L'} ${xOf(n).toFixed(2)} ${yOf(fn(n)).toFixed(2)}`).join(' ');
  const linePath = pathFor((n) => n);
  const sqrtPath = pathFor((n) => 2 * Math.sqrt(n));
  const logPath = pathFor((n) => Math.max(1, Math.log2(n)));

  const xTicks = [8, 32, 128, 512, 2048];
  const yTicks = [1, 8, 64, 512, 2048];

  return (
    <div className="viz-panel gc-scaling">
      <svg viewBox={`0 0 ${W} ${H}`} className="gc-chart" preserveAspectRatio="xMidYMid meet">
        {/* axes */}
        <line x1={padL} y1={padT + innerH} x2={padL + innerW} y2={padT + innerH}
              stroke="var(--post-border)" strokeWidth="1" />
        <line x1={padL} y1={padT} x2={padL} y2={padT + innerH}
              stroke="var(--post-border)" strokeWidth="1" />

        {/* gridlines + ticks */}
        {xTicks.map((n) => (
          <g key={`x-${n}`}>
            <line x1={xOf(n)} y1={padT} x2={xOf(n)} y2={padT + innerH}
                  stroke="var(--post-border)" strokeOpacity="0.5" strokeDasharray="2 4" />
            <text x={xOf(n)} y={padT + innerH + 16} textAnchor="middle" fontSize="11"
                  fill="var(--post-text-mut)" fontFamily="var(--post-mono)">{n}</text>
          </g>
        ))}
        {yTicks.map((m) => (
          <g key={`y-${m}`}>
            <line x1={padL} y1={yOf(m)} x2={padL + innerW} y2={yOf(m)}
                  stroke="var(--post-border)" strokeOpacity="0.5" strokeDasharray="2 4" />
            <text x={padL - 8} y={yOf(m) + 4} textAnchor="end" fontSize="11"
                  fill="var(--post-text-mut)" fontFamily="var(--post-mono)">{m}</text>
          </g>
        ))}

        {/* axis labels */}
        <text x={padL + innerW / 2} y={H - 8} textAnchor="middle" fontSize="12"
              fill="var(--post-text-mid)">depth n (layers)</text>
        <text x={14} y={padT + innerH / 2} textAnchor="middle" fontSize="12"
              fill="var(--post-text-mid)"
              transform={`rotate(-90 14 ${padT + innerH / 2})`}>peak activation memory</text>

        {/* curves */}
        <path d={linePath} className="gc-curve gc-curve-linear" />
        <path d={sqrtPath} className="gc-curve gc-curve-sqrt" />
        <path d={logPath} className="gc-curve gc-curve-log" />

        {/* curve labels at right edge */}
        <text x={xOf(nMax) - 6} y={yOf(nMax) - 8} fontSize="11" textAnchor="end"
              fill="var(--gc-c-linear)" fontFamily="var(--post-mono)">
          O(n) — vanilla backprop
        </text>
        <text x={xOf(nMax) - 6} y={yOf(2 * Math.sqrt(nMax)) - 8} fontSize="11" textAnchor="end"
              fill="var(--gc-c-sqrt)" fontFamily="var(--post-mono)">
          O(√n) — √n checkpointing
        </text>
        <text x={xOf(nMax) - 6} y={yOf(Math.log2(nMax)) - 8} fontSize="11" textAnchor="end"
              fill="var(--gc-c-log)" fontFamily="var(--post-mono)">
          O(log n) — recursive
        </text>

        {/* highlight: 1000-layer ResNet */}
        <line x1={xOf(1000)} y1={padT} x2={xOf(1000)} y2={padT + innerH}
              stroke="var(--post-accent)" strokeWidth="1" strokeDasharray="4 4" strokeOpacity="0.6" />
        <text x={xOf(1000) - 6} y={padT + 14} fontSize="11" textAnchor="end"
              fill="var(--post-accent)" fontFamily="var(--post-mono)">
          1000-layer ResNet
        </text>
      </svg>
      <p className="gc-caption">
        Both axes are <em>logarithmic</em>. The vanilla line is the diagonal —
        memory scales with depth. The √n line bends below it; the recursive log line
        flattens out almost completely. For a 1000-layer net: ~1000 units → ~63 →
        ~10. The catch is the extra compute scales the opposite way.
      </p>
    </div>
  );
}

/* ============================================================
   Code block — torch.utils.checkpoint usage
   ============================================================ */
const CODE = `# Drop activations in a transformer block; recompute on backward.
import torch
from torch.utils.checkpoint import checkpoint

class Block(torch.nn.Module):
    def __init__(self, d, h):
        super().__init__()
        self.attn = SelfAttention(d, h)
        self.mlp  = MLP(d)

    def forward(self, x):
        # \`checkpoint\` wraps a sub-graph: only its inputs are saved.
        # Its internal activations are re-derived during backward.
        #
        # use_reentrant=False — both versions do the same thing
        # (skip activations on forward, recompute on backward). What
        # differs is how the recompute is plumbed into autograd:
        #
        #   True  (legacy): wraps the block in a custom op. On backward
        #                   PyTorch starts a SEPARATE nested autograd
        #                   pass inside the op to do the recompute.
        #   False (modern): hooks the "saved tensor" slot. During
        #                   forward it stores a recipe instead of the
        #                   tensor; on backward the recipe runs inline
        #                   in the SAME autograd pass.
        #
        # Always pick False — it handles kwargs, higher-order grads,
        # torch.compile, and preserves dropout RNG state correctly.
        x = x + checkpoint(self.attn, x, use_reentrant=False)
        x = x + checkpoint(self.mlp,  x, use_reentrant=False)
        return x
`;

function CodeBlock() {
  return (
    <details className="post-code-details gc-code">
      <summary>
        <span className="post-code-summary-icon">›</span>
        <span>PyTorch — gradient checkpointing in one decorator</span>
        <span className="post-code-summary-hint">click to expand</span>
      </summary>
      <SyntaxHighlighter
        language="python"
        style={oneLight}
        customStyle={{ background: 'transparent', margin: 0, padding: 0, fontSize: '13.5px' }}
        codeTagProps={{ style: { background: 'transparent', textShadow: 'none' } }}
      >
        {CODE}
      </SyntaxHighlighter>
    </details>
  );
}

/* ============================================================
   Page
   ============================================================ */
export default function GradientCheckpointing() {
  usePageMeta({
    title: 'Gradient Checkpointing: Trading FLOPs for Memory',
    description: 'How dropping most activations on the forward pass and recomputing them on the backward turns O(n) memory into O(√n) for one extra forward pass — the trick that lets a 1000-layer ResNet fit on a single GPU.',
    slug: 'gradient-checkpointing',
    publishedDate: '2026-06-04',
    keywords: ['gradient checkpointing', 'activation recomputation', 'memory', 'training', 'sublinear memory'],
  });

  return (
    <article className="post-2026 gc-post">
      <div className="post-wrap">
        <div className="post-hero">
          <div className="post-series-tag">
            <span className="post-live-dot" aria-hidden="true" />Scaling Training · Part 1 of 5
          </div>
          <h1>Gradient Checkpointing: Trading FLOPs for Memory</h1>
          <p className="post-lede">
            A 1000-layer ResNet wants <strong>48 GB</strong> of activations during
            training — more than any single GPU has. Same network, one trick:
            7 GB. The trick is to <em>not save</em> the activations on the forward
            pass, and to redo the forward locally whenever the backward pass
            wants them back.
          </p>
          <div className="post-byline">
            By <strong>Bernhard Walser</strong> &amp;{' '}
            <a className="post-link" href="https://www.anthropic.com/claude" target="_blank" rel="noreferrer"><strong>Claude</strong></a>
            {' '}(Anthropic) · co-written and co-designed ·{' '}
            <a className="post-link" href="https://www.linkedin.com/in/bernhardwalser/" target="_blank" rel="noreferrer">LinkedIn</a>
            {' · '}
            <a className="post-link" href="https://github.com/berniwal" target="_blank" rel="noreferrer">GitHub</a>
          </div>
        </div>

        <h2 className="reveal">Why activations cost so much</h2>
        <p>
          Backprop needs every layer's output kept around until that layer's
          backward arrives. With <Katex tex="n" /> layers, that's{' '}
          <Katex tex="n" /> tensors held in memory at peak — and for a
          transformer those tensors are big, because activation size scales
          with batch × sequence × hidden. See{' '}
          <a className="post-link" href="/blog/training-cost">Training Cost of a Transformer</a>{' '}
          for the bucket breakdown; activations alone often dominate
          parameters + optimizer state combined.
        </p>
        <p>
          The depth axis is the painful one. Double the layers, double the
          activation memory — even though the parameter count and the FLOPs
          per step only roughly double too. There's no obvious slack to give.
        </p>

        <h2 className="reveal">The idea, in one sentence</h2>
        <p>
          <strong>Drop most forward activations. Recompute them on the way
          back.</strong> The forward pass only keeps a sparse set of{' '}
          <em>checkpoints</em>. When the backward pass reaches a layer whose
          input you discarded, you find the nearest stored checkpoint to the
          left of it, re-run the forward from there until you reach the layer
          you need, then do the gradient step — and free the recomputed
          activations once you're done with them.
        </p>
        <p>
          So you pay one extra forward pass over the training step, in
          exchange for never holding more than a sliver of the network's
          activations at once.
        </p>

        <h2 className="reveal">The trade-off, live</h2>
        <p>
          Slide the number of stored checkpoints below.{' '}
          <span className="gc-mut">k = 0</span> means you keep only the
          input — minimal memory, maximum recompute.{' '}
          <span className="gc-mut">k = n − 2</span> means you keep everything
          — full memory, zero overhead.
        </p>
        <TradeOff />

        <h2 className="reveal">Why √n is the sweet spot</h2>
        <p>
          Split <Katex tex="n" /> layers into <Katex tex="k" /> equal-sized
          segments. The forward pass stores <Katex tex="k" /> segment
          boundaries. The backward pass walks segments right-to-left; inside
          each one it re-runs the forward, which temporarily holds about{' '}
          <Katex tex="n/k" /> activations in flight.
        </p>
        <Katex
          block
          tex={'\\text{peak memory} \\;=\\; \\underbrace{k}_{\\text{stored}} \\;+\\; \\underbrace{n/k}_{\\text{recompute buffer}}'}
        />
        <p>
          That's the sum of two terms moving in opposite directions: more
          checkpoints shrink the recompute buffer but cost memory of their
          own. Take the derivative with respect to <Katex tex="k" /> and set
          it to zero —
        </p>
        <Katex
          block
          tex={'\\frac{d}{dk}\\!\\left(k + \\frac{n}{k}\\right) = 1 - \\frac{n}{k^2} = 0 \\;\\Longrightarrow\\; k^* = \\sqrt{n}'}
        />
        <p>
          The minimum sits at <Katex tex="k = \sqrt{n}" />, with peak memory{' '}
          <Katex tex="2\sqrt{n}" />. That's the headline result of{' '}
          <a className="post-link" href="https://arxiv.org/abs/1604.06174" target="_blank" rel="noreferrer">
            Chen et al. 2016
          </a>: <strong>sublinear memory for one extra forward pass</strong>.
        </p>

        <h2 className="reveal">How much slower is it really?</h2>
        <p>
          A vanilla training step is roughly one forward plus one backward,
          and backward is about 2× the cost of a forward (it does both the
          activation gradient and the weight gradient). So the baseline is
          three "forward units" per step. Adding one extra forward for
          recompute brings it to four — a <strong>~33%</strong> slowdown.
          That's the number the paper measures, give or take a few points
          depending on layer mix.
        </p>

        <h2 className="reveal">Scaling — and the recursive trick</h2>
        <p>
          The picture below puts all three regimes on the same axes. The
          vanilla line tracks depth directly, √n bends down hard, and a
          third option flattens almost completely. That third one is the
          recursive variant — and it deserves its own walkthrough, because
          it's the slickest part of the paper.
        </p>
        <ScalingChart />

        <h3 className="reveal">Divide and conquer the chain</h3>
        <p>
          The √n version stored evenly-spaced checkpoints across the whole
          chain. The recursive version uses a different shape: think of it
          as <strong>merge-sort, but for back-prop</strong>.
        </p>
        <p>
          To back-prop through a chain of <Katex tex="L" /> layers, given
          the input activation:
        </p>
        <ol className="gc-walk">
          <li>
            <strong>Forward to the middle.</strong> Run layers 1 through{' '}
            <Katex tex="L/2" />. Save <em>only</em> the activation at the
            midpoint.
          </li>
          <li>
            <strong>Back-prop the right half.</strong> Call the same
            procedure recursively on layers <Katex tex="L/2+1" /> through{' '}
            <Katex tex="L" />, using the saved midpoint as its starting
            input.
          </li>
          <li>
            <strong>Back-prop the left half.</strong> Call recursively on
            layers 1 through <Katex tex="L/2" />, using the original input.
          </li>
        </ol>
        <p>
          That's the whole algorithm. Each call saves one activation — the
          midpoint — and then makes two recursive calls. The recursion
          bottoms out when a "chain" is one layer: just recompute it and
          do the gradient.
        </p>

        <h3 className="reveal">Why it's <Katex tex="O(\log n)" /> memory</h3>
        <p>
          At any moment, the call stack is some chain of nested
          half-the-previous-size calls. Each pending call is holding onto
          exactly one saved activation (its midpoint), waiting for its two
          child calls to finish. The deepest the stack can get is{' '}
          <Katex tex="\log_2 n" /> — that's how many times you can halve{' '}
          <Katex tex="n" /> before you reach 1.
        </p>
        <p>
          So peak memory ≈ <Katex tex="\log_2 n" /> saved activations. For
          a 16-layer chain, four activations. For a 1000-layer net, ten.
        </p>

        <h3 className="reveal">Why it's <Katex tex="O(n \log n)" /> compute</h3>
        <p>
          The same merge-sort accounting applies. Each call does about{' '}
          <Katex tex="L" /> work in its midpoint forward, then hands off
          two half-sized calls. That's the <Katex tex="T(L) = 2T(L/2) + L" />
          {' '}recurrence, which solves to <Katex tex="O(L \log L)" />. Total
          compute is the baseline forward + backward (<Katex tex="O(n)" />)
          plus the recompute (<Katex tex="O(n \log n)" />), so the bottleneck
          is the recompute.
        </p>
        <p>
          A concrete sanity check: for a 16-layer chain, baseline is{' '}
          <Katex tex="3n = 48" /> forward-units. Recursive checkpointing
          piles on roughly <Katex tex="n \log_2 n = 64" /> more. ~2.3×
          slowdown — a real cost.
        </p>
        <p>
          In practice almost nobody recurses. The <Katex tex="\sqrt{n}" />{' '}
          point is already a 6–10× memory win on a typical net, and the
          compute overhead stays flat at ~33%. Recursion buys you a few
          extra GB for 2–3× more slowdown — usually you'd rather spend
          that compute on a larger batch.
        </p>

        <h2 className="reveal">In practice</h2>
        <p>
          PyTorch ships this as <code>torch.utils.checkpoint.checkpoint</code>.
          You wrap a block, and PyTorch arranges for the wrapped sub-graph's
          internal activations to be recomputed on backward.
        </p>
        <CodeBlock />
        <p>
          HuggingFace's <code>model.gradient_checkpointing_enable()</code> is
          the same trick applied per transformer block — the placement the
          paper proves is optimal happens to be exactly "one checkpoint per
          block", which is what you want anyway. FSDP and DeepSpeed both
          compose with checkpointing cleanly; the next post in this series
          looks at how those <em>shard</em> the memory that's left.
        </p>

        <h2 className="reveal">Caveats and edge cases</h2>
        <p>
          A few things to know before you reach for it. <strong>Dropout and
          other stochastic layers</strong> need their RNG state preserved so
          the recompute lands on the same random draws as the original
          forward — PyTorch's <code>checkpoint</code> does this for you, but
          custom recompute code can drop it on the floor.{' '}
          <strong>Non-reentrant mode</strong>{' '}
          (<code>use_reentrant=False</code>) is the version you want;
          reentrant mode has subtle issues with autograd's higher-order
          graphs and is being phased out.
        </p>
        <p>
          Activations that are <em>already cheap</em> — LayerNorm, residual
          adds, ReLU — give back almost no memory when dropped, because the
          surrounding matmul activations dominate. The classic placement
          (checkpoint each transformer block as a whole, not each op inside
          it) gets most of the win for a fraction of the bookkeeping.
        </p>

        <h2 className="reveal">References</h2>
        <div className="post-refs">
          <div className="head ref-cite">Cite</div>
          <div className="head ref-title">Title</div>
          <div className="head ref-link">Link</div>

          <div className="ref-cite">Chen et al. 2016</div>
          <div>
            <div className="ref-title">
              <a href="https://arxiv.org/abs/1604.06174" target="_blank" rel="noreferrer">Training Deep Nets with Sublinear Memory Cost</a>
            </div>
            <div className="ref-note">The original paper. Derives the √n placement and the recursive O(log n) extension.</div>
          </div>
          <div className="ref-link"><a href="https://arxiv.org/abs/1604.06174" target="_blank" rel="noreferrer">arxiv.org/abs/1604.06174</a></div>

          <div className="ref-cite">PyTorch docs</div>
          <div>
            <div className="ref-title">
              <a href="https://pytorch.org/docs/stable/checkpoint.html" target="_blank" rel="noreferrer">torch.utils.checkpoint</a>
            </div>
            <div className="ref-note">API reference. Note the <code>use_reentrant=False</code> flag.</div>
          </div>
          <div className="ref-link"><a href="https://pytorch.org/docs/stable/checkpoint.html" target="_blank" rel="noreferrer">pytorch.org</a></div>

          <div className="ref-cite">Earlier posts</div>
          <div>
            <div className="ref-title">
              <a href="/blog/training-cost">Training Cost of a Transformer</a>
            </div>
            <div className="ref-note">Where the activation-memory bucket comes from in the first place.</div>
          </div>
          <div className="ref-link"><a href="/blog/training-cost">/training-cost</a></div>
        </div>

        <footer className="post-footer">
          <p>
            <strong>Part 1 of Scaling Training</strong> · Next:{' '}
            <a className="post-link" href="/blog/zero"><em>ZeRO — sharding the optimizer</em></a>.
          </p>
          <p style={{ marginBottom: 0 }}>
            Bernhard Walser · ML Engineer, Digitec Galaxus · ETH Computer Science ·{' '}
            <a className="post-link" href="https://www.linkedin.com/in/bernhardwalser/" target="_blank" rel="noreferrer">LinkedIn</a>
            {' · '}
            <a className="post-link" href="https://github.com/berniwal" target="_blank" rel="noreferrer">GitHub</a>
          </p>
        </footer>
      </div>
    </article>
  );
}
